import React from 'react';
import dayjs from 'dayjs';
import { capitalize, isEmpty } from 'lodash';
import shortid from 'shortid';

import { showLightbox } from 'partial/components/Modal';
import bizCorporation from 'assets/images/icons/biz-corporation.svg';
import bizIndividual from 'assets/images/icons/biz-individual.svg';
import bizSole from 'assets/images/icons/biz-sole.svg';
import bizGovernment from 'assets/images/icons/biz-partnership.svg';

export const MERCHANT_LIST = 'MERCHANTS/list';
export const MERCHANT_PAGER = 'MERCHANTS/pager';
export const MERCHANT_FILTER = 'MERCHANTS/filter';
export const MERCHANT_ITEM = 'MERCHANTS/item';

export const MERCHANT_SETTLEMENT_GET = 'MERCHANTS/settlement_get';
export const MERCHANT_SETTLEMENT_ADD = 'MERCHANTS/settlement_add';
export const MERCHANT_SETTLEMENT_LIST = 'MERCHANTS/settlement_list';
export const MERCHANT_SETTLEMENT_PAGER = 'MERCHANTS/settlement_pager';
export const MERCHANT_SETTLEMENT_FILTER = 'MERCHANTS/settlement_filter';

export const MERCHANT_ORG_SETTLEMENT_GET = 'MERCHANTS/org_settlement_get';
export const MERCHANT_ORG_SETTLEMENT_LIST = 'MERCHANTS/org_settlement_list';
export const MERCHANT_ORG_SETTLEMENT_PAGER = 'MERCHANTS/org_settlement_pager';
export const MERCHANT_ORG_SETTLEMENT_FILTER = 'MERCHANTS/org_settlement_filter';

export const MERCHANT_API_KEY_LIST = 'MERCHANTS/api_key_list';
export const MERCHANT_API_KEY_PAGER = 'MERCHANTS/api_key_pager';
export const MERCHANT_API_KEY_FILTER = 'MERCHANTS/api_key_filter';
export const MERCHANT_API_KEY_ITEM = 'MERCHANTS/api_key_item';

export const MERCHANT_GENERAL_INFO = 'MERCHANTS/general_info';
export const MERCHANT_BUSINESS_INFO = 'MERCHANTS/business_info';

export const COUNT_CARD = 'MERCHANT/count_card';

/* MERCHANT PROFILE */
export const BUSINESS_TYPE_IMAGE = {
  CORPORATION: bizCorporation,
  SOLE_PROPRIETORSHIP: bizSole,
  INDIVIDUAL: bizIndividual,
  GOVERNMENT: bizGovernment,
};

export const BUSINESS_TYPE_LABEL = {
  CORPORATION: 'Corporation',
  SOLE_PROPRIETORSHIP: 'Sole Proprietorship/Partnership',
  INDIVIDUAL: 'Individual',
  GOVERNMENT: 'Government',
};

export const REPRESENTATIVE_INFORMATION_FORMAT = (type) => {
  if (type === 'CORPORATION') {
    return [
      {
        label: 'Full Name',
        key: (row) => (
          <span>
            {row?.first_name} {row?.last_name}
          </span>
        ),
      },
      {
        label: 'Beneficial Owner',
        key: (row) => <span>{row?.is_beneficial_owner ? 'Yes' : 'No'}</span>,
      },
    ];
  }

  return [
    {
      label: 'Full Name',
      key: (row) => (
        <span>
          {row?.first_name} {row?.last_name}
        </span>
      ),
    },
  ];
};

export const HOME_ADDRESS_FORMAT = [
  {
    label: 'Complete Address',
    key: (row) => (
      <span>
        {row?.unit_no ? `${row?.unit_no}, ` : ''}
        {row?.house_no ? `${row?.house_no}, ` : ''}
        {row?.building_name ? `${row?.building_name}, ` : ''}
        {row?.street ? `${row?.street}, ` : ''}
        {row?.district ? `${row?.district}, ` : ''}
        {row?.postal_code ? `${row?.postal_code}, ` : ''} Brunei
      </span>
    ),
  },
];

export const imagePreview = ({
  key,
  label,
  span = 1,
  className = 'grid grid-cols-2',
}) => ({
  label,
  key: (row) => (
    <div className={className}>
      {!isEmpty(row?.[key]) &&
        row?.[key].map((item) => {
          const isImage = item?.is_image || item?.isImage;

          return (
            <div
              className="aspect-w-4 aspect-h-2 rounded-md overflow-hidden bg-gray-200 mb-4 mr-4"
              key={`${key}_${shortid.generate()}`}
            >
              <div
                aria-hidden="true"
                onClick={() => {
                  showLightbox({
                    title: label,
                    content: item?.url,
                    isImage,
                  });
                }}
                className="absolute inset-0 z-10 cursor-pointer group transition hover:bg-black hover:bg-opacity-30"
              >
                <span className="p-3 text-sm text-gray-100 hidden group-hover:block">
                  Click to view
                </span>
              </div>
              {isImage ? (
                <img
                  src={item?.url}
                  className="object-contain flex-shrink-0"
                  alt=""
                />
              ) : (
                <iframe
                  src={item?.url}
                  frameBorder="0"
                  title="File Preview"
                  allowtransparency="true"
                  style={{
                    overflow: 'hidden',
                    height: '100%',
                    width: '100%',
                  }}
                  height="100%"
                  width="100%"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowFullScreen
                />
              )}
            </div>
          );
        })}
    </div>
  ),
  span,
});

export const UPLOADED_ID_FORMAT = [
  imagePreview({
    label: 'Valid Identification Documents',
    key: 'owner_id_image_url',
  }),
];

export const BANK_DETAILS_FORMAT = [
  {
    label: 'Bank',
    key: 'bank_name',
  },
  {
    label: 'Account Name',
    key: 'bank_account_name',
  },
  {
    label: 'Account Number',
    key: 'bank_account_number',
  },
];

const BUSINESS_INFO_CORPORATION = [
  {
    label: 'Business Legal Name',
    key: 'business_legal_name',
  },
  {
    label: 'Business Registration Number',
    key: 'business_registration_number',
  },
  {
    label: 'Date of Incorporation',
    key: (row) => (
      <span>
        {dayjs(row?.business_incorporation_date).format('MMMM DD, YYYY')}
      </span>
    ),
  },
  imagePreview({
    label: 'Certification of Corporation',
    key: 'cert_of_corporation_imge_url',
    span: 3,
  }),
  imagePreview({
    label: 'Form X',
    key: 'form_x_image_url',
    span: 3,
  }),
  imagePreview({
    label: "Local's Director IC",
    key: 'business_local_director_ic_image_url',
    className: 'grid grid-cols-2 gap-x-2',
    span: 3,
  }),
];

const BUSINESS_INFO_SOLE_PROPRIETORHSIP = [
  {
    label: 'Business Legal Name',
    key: 'business_legal_name',
  },
  {
    label: 'Business Registration Number',
    key: 'business_registration_number',
  },
  {
    label: 'Date of Incorporation',
    key: 'business_incorporation_date',
  },
  imagePreview({
    label:
      'Business Registration (16 & 17) or Business Registration and Registry of Business Name',
    key: 'business_cert_image_url',
    span: 3,
  }),
  imagePreview({
    label: "Local's Owner IC",
    key: 'business_local_owner_ic_image_url',
    span: 3,
  }),
];

const BUSINESS_INFO_INDIVIDUAL = [
  {
    label: 'Business Duration',
    key: 'business_duration',
    span: 3,
  },
];

const BUSINESS_INFO_GOVERNMENT = [
  imagePreview({
    label: 'Ministry Appointment Letter',
    key: 'ministry_appointment_letter_img_url',
    span: 3,
  }),
  imagePreview({
    label: 'Ministry Letter for Appointed Authorised Person',
    key: 'ministry_letter_for_appointed_img_url',
    span: 3,
  }),
  imagePreview({
    label: 'IC Authorised Person',
    key: 'ic_authorised_person_img_url',
    span: 3,
  }),
];

export const GOVERNMENT_INFO_FORMAT = [
  {
    label: 'Government Ministry',
    key: 'government_ministry',
    span: 3,
  },
  {
    label: 'Government Official Letter',
    key: 'government_official_letter_refno',
    span: 1,
  },
  {
    label: 'Date of Government Official Letter',
    key: (row) => (
      <span>
        {dayjs(row?.date_of_government_official_letter).format('MMMM DD, YYYY')}
      </span>
    ),
    span: 2,
  },
  {
    label: 'Authorised Name of Contact Person',
    key: 'authorised_name_of_contact_person',
    span: 1,
  },
  {
    label: 'Authorised Contact Person IC Number',
    key: 'authorised_contact_person_ic_no',
    span: 2,
  },
];

export const BUSINESS_INFO_FORMAT = {
  CORPORATION: BUSINESS_INFO_CORPORATION,
  SOLE_PROPRIETORSHIP: BUSINESS_INFO_SOLE_PROPRIETORHSIP,
  INDIVIDUAL: BUSINESS_INFO_INDIVIDUAL,
  GOVERNMENT: BUSINESS_INFO_GOVERNMENT,
};
/* MERCHANT PROFILE END */

/* MERCHANT PAYMENT ATTACHMENTS START */
export const LIST_PAYMENT_CATEGORIES = 'MERCHANT/get_attached_categories';
export const LIST_PAYMENT_CATEGORY_CHANNELS = (category_uuid) =>
  `MERCHANT/get_category_channels?key=${category_uuid}`;
export const TOGGLE_CHANNEL_STATUS = 'MERCHANT/toggle_attached_channel_status';

export const TOGGLE_CATEGORY_STATUS =
  'MERCHANT/toggle_attached_cateogry_status';

export const LIST_SELECT_PAYMENT_CHANNELS =
  'MERCHANT/list_select_channels_options';

export const FEE_RATE_CLASSIFICATIONS = ['CHANNEL', 'SYSTEM', 'PARTNER'];
/* MERCHANT PAYMENT ATTACHMENTS END */

/* SETTLEMENTS */
const STATUS_CLASS = {
  success: 'text-secondary-800',
  pending: 'text-yellow-500',
};

export const PENDING_SETTLEMENT_FORMAT = {
  organization: [
    {
      label: 'Date Request',
      key: 'date_request',
    },
    {
      label: 'Request Ref No.',
      key: 'request_ref_no',
    },
    {
      label: 'Biller Name',
      key: (row) => row?.merchant_details?.name,
    },
    {
      label: 'Amount Requested',
      key: 'amount_request',
    },
    {
      label: 'Requested by',
      key: 'requested_by',
    },
    {
      label: 'Settlement Status',
      key: (row) => (
        <span className={`${STATUS_CLASS[row?.settlement_status]}`}>
          {capitalize(row?.settlement_status)}
        </span>
      ),
    },
  ],
  biller: [
    {
      label: 'Date Request',
      key: 'date_request',
    },
    {
      label: 'Request Ref No.',
      key: 'request_ref_no',
      span: 2,
    },
    {
      label: 'Amount Requested',
      key: 'amount_request',
    },
    {
      label: 'Requested by',
      key: 'requested_by',
    },
    {
      label: 'Settlement Status',
      key: (row) => (
        <span className={`${STATUS_CLASS[row?.settlement_status]}`}>
          {capitalize(row?.settlement_status)}
        </span>
      ),
    },
  ],
};

export const SUCCESS_SETTLEMENT_FORMAT = [
  {
    label: 'Date Received Settlement',
    key: 'date_received_settlement',
  },
  {
    label: 'Settlement Ref No.',
    key: 'settlement_ref_no',
  },
  {
    label: 'Accumulated Amount',
    key: 'accumulated_amount',
  },
  {
    label: 'Settled by',
    key: 'settled_by',
    span: 3,
  },
  imagePreview({
    label: 'Proof of Payment',
    key: 'proof_of_payment',
    className: 'grid grid-cols-3',
    span: 3,
  }),
];
